import { TEMPLATES } from '../animation/procedural/spec';
import { createId } from '../lib/ids';
import { isPoseChannel, type Animation, type AvSession, type Project } from './schemas';

/**
 * Reference integrity between animations and scenes (Phase 3B). Policy:
 *  - deleting objects (directly, with their character or image, or with their scene)
 *    removes their tracks; deleting a scene removes its animations;
 *  - duplicating a scene copies its animations with new animation/track ids and the
 *    copied objects' ids;
 *  - duplicating an object does not copy tracks (the copy is a static base object).
 * Kept free of sceneOps so scene operations can call it without an import cycle.
 */

/**
 * Drops animations of missing scenes and tracks of missing (or no longer valid) objects,
 * then the audio-visual session references that pointed at them (`pruneSessions`).
 */
export function pruneAnimations(project: Project): Project {
  return pruneSessions(pruneAnimationTracks(project));
}

function pruneAnimationTracks(project: Project): Project {
  if (project.animations.length === 0) return project;
  const scenes = new Map(project.scenes.map((s) => [s.id, new Map(s.objects.map((o) => [o.id, o]))]));
  let changed = false;
  const animations: Animation[] = [];
  for (const a of project.animations) {
    const objects = scenes.get(a.sceneId);
    if (!objects) {
      changed = true;
      continue;
    }
    const tracks = a.tracks.filter((t) => {
      const o = objects.get(t.objectId);
      return !!o && (!isPoseChannel(t.channel) || o.type === 'character');
    });
    if (tracks.length !== a.tracks.length) {
      changed = true;
      animations.push({ ...a, tracks });
    } else animations.push(a);
  }
  return changed ? { ...project, animations } : project;
}

/** Copies of a scene's animations for a duplicated scene (object ids remapped). */
export function copySceneAnimations(project: Project, sourceSceneId: string, newSceneId: string, objectIds: ReadonlyMap<string, string>): Animation[] {
  return project.animations
    .filter((a) => a.sceneId === sourceSceneId)
    .map((a) => ({
      ...structuredClone(a),
      id: createId('anm'),
      sceneId: newSceneId,
      tracks: a.tracks
        .filter((t) => objectIds.has(t.objectId))
        .map((t) => ({ ...structuredClone(t), id: createId('trk'), objectId: objectIds.get(t.objectId)! })),
    }));
}

// ---------------------------------------------------------------- Audio-visual sessions (Phase 4A)

/**
 * Drops sessions of missing scenes, unlinks missing animations (or animations moved to
 * another scene), and removes mappings and motion cues whose object is gone or no longer
 * fits (pose reactions and character-only cues need a character).
 */
export function pruneSessions(project: Project): Project {
  if (!project.avSessions?.length) return project;
  const scenes = new Map(project.scenes.map((s) => [s.id, new Map(s.objects.map((o) => [o.id, o]))]));
  const animations = new Map(project.animations.map((a) => [a.id, a]));
  let changed = false;
  const avSessions: AvSession[] = [];
  for (const s of project.avSessions) {
    const objects = scenes.get(s.sceneId);
    if (!objects) {
      changed = true;
      continue;
    }
    const animationOk = s.animationId === null || animations.get(s.animationId)?.sceneId === s.sceneId;
    const mappings = s.mappings.filter((m) => {
      if (m.target.kind === 'scene') return true;
      const o = objects.get(m.target.objectId);
      return !!o && (m.reaction !== 'pose' || o.type === 'character');
    });
    const motionCues = s.motionCues.filter((c) => {
      const o = objects.get(c.objectId);
      return !!o && (!TEMPLATES[c.template].characterOnly || o.type === 'character');
    });
    if (!animationOk || mappings.length !== s.mappings.length || motionCues.length !== s.motionCues.length) {
      changed = true;
      avSessions.push({ ...s, animationId: animationOk ? s.animationId : null, mappings, motionCues });
    } else avSessions.push(s);
  }
  return changed ? { ...project, avSessions } : project;
}
